/**
 * Ephemeral "the parent wanted to pay at signup" hint (plan 030).
 *
 * Set by SignupRoute + LoginRoute when the URL carries `intent=paid`;
 * read by MnemonicShowRoute to change the "Continue" label + destination,
 * and by /checkout/redirect to know which cadence to open in Stripe.
 *
 * Backed by sessionStorage so a tab reload mid-onboarding doesn't lose
 * the state. Cleared once the checkout finishes (success or cancel), or
 * on explicit dashboard entry.
 */

import { signal } from '@preact/signals';

export type Cadence = 'monthly' | 'annual';

export interface CheckoutIntent {
  paid: boolean;
  cadence: Cadence;
}

const STORAGE_KEY = 'backstop.checkout-intent.v1';

function hydrate(): CheckoutIntent | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    return JSON.parse(raw) as CheckoutIntent;
  } catch {
    return null;
  }
}

export const checkoutIntent = signal<CheckoutIntent | null>(hydrate());

export function setCheckoutIntent(intent: CheckoutIntent): void {
  checkoutIntent.value = intent;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // sessionStorage disabled — stays in-memory.
  }
}

export function clearCheckoutIntent(): void {
  checkoutIntent.value = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * Convenience: parse `?cadence=X&intent=paid` from the current URL and,
 * if `intent=paid` is present, persist the signal. Returns whether it
 * captured intent so callers can decide on post-signup routing.
 */
export function captureIntentFromUrl(): CheckoutIntent | null {
  if (typeof window === 'undefined') return null;
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('intent') !== 'paid') return null;
    const cadenceParam = p.get('cadence');
    const cadence: Cadence = cadenceParam === 'annual' ? 'annual' : 'monthly';
    const intent: CheckoutIntent = { paid: true, cadence };
    setCheckoutIntent(intent);
    return intent;
  } catch {
    return null;
  }
}
